import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useActions } from "../context/ActionsContext";
import api from "../lib/api";
import {
  LayoutDashboard, Receipt, Wallet, ArrowLeftRight, Undo2, BookOpen, Users, UserCog,
  Landmark, PieChart, Sparkles, Database, Shield, Settings as SettingsIcon, LogOut,
  Menu, X, Plus, HandCoins, Search, Moon, Sun, ChevronDown, FileText, TrendingUp,
  Bell, CheckSquare, AlertTriangle,
} from "lucide-react";
import { Button } from "./ui/button";

const NAV = [
  { section: null, items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard, end: true }] },
  {
    section: "Accounting", items: [
      { to: "/transactions", label: "Transactions", icon: BookOpen },
      { to: "/advances", label: "Advances", icon: HandCoins },
      { to: "/approvals", label: "Approvals", icon: CheckSquare },
    ],
  },
  {
    section: "Candidates", items: [
      { to: "/candidates", label: "All Candidates", icon: Users },
      { to: "/reports/outstanding", label: "Outstanding", icon: TrendingUp },
    ],
  },
  {
    section: "Payees & Agencies", items: [
      { to: "/payees", label: "All Payees", icon: UserCog },
    ],
  },
  {
    section: "Accounts", items: [
      { to: "/accounts", label: "All Accounts", icon: Landmark },
      { to: "/reconciliation", label: "Reconciliation", icon: ArrowLeftRight },
    ],
  },
  {
    section: "Reports", items: [
      { to: "/reports/daily", label: "Daily Report", icon: FileText },
      { to: "/reports/weekly", label: "Weekly Report", icon: FileText },
      { to: "/reports/monthly", label: "Monthly Report", icon: FileText },
      { to: "/reports/period", label: "Period Report", icon: FileText },
      { to: "/reports/pnl", label: "Profit & Loss", icon: PieChart },
      { to: "/reports/cashflow", label: "Cash Flow", icon: ArrowLeftRight },
    ],
  },
  { section: null, items: [{ to: "/ai", label: "AI Assistant", icon: Sparkles }] },
  {
    section: "Master Data", items: [
      { to: "/master", label: "Categories & Branches", icon: Database },
    ],
  },
  {
    section: "Administration", items: [
      { to: "/users", label: "Users", icon: Shield, admin: true },
      { to: "/audit", label: "Audit Logs", icon: Shield, admin: true },
      { to: "/settings", label: "Settings", icon: SettingsIcon, admin: true },
    ],
  },
];

const QUICK = [
  { type: "receive", label: "Receive Payment", icon: Receipt },
  { type: "expense", label: "Add Expense", icon: Wallet },
  { type: "advance_candidate", label: "Candidate Advance", icon: HandCoins },
  { type: "give_advance", label: "Give Advance", icon: ArrowLeftRight },
  { type: "refund", label: "Refund Candidate", icon: Undo2 },
  { type: "transfer", label: "Transfer Money", icon: ArrowLeftRight },
  { type: "add_candidate", label: "Add Candidate", icon: Users },
];

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const { open } = useActions();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [dark, setDark] = useState(localStorage.getItem("prabhat_theme") === "dark");
  const [alerts, setAlerts] = useState([]);
  const [alertsOpen, setAlertsOpen] = useState(false);

  useEffect(() => {
    const fetchAlerts = () => api.get("/alerts").then((r) => setAlerts(r.data.alerts)).catch(() => {});
    fetchAlerts();
    window.addEventListener("prabhat:refresh", fetchAlerts);
    return () => window.removeEventListener("prabhat:refresh", fetchAlerts);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("prabhat_theme", dark ? "dark" : "light");
  }, [dark]);

  const initials = (user?.name || "U").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const roleLabel = { super_admin: "Super Admin", admin: "Admin", accountant: "Accountant", data_entry: "Data Entry", viewer: "Viewer" }[user?.role];

  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-slate-950">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 h-screen w-64 bg-[#0F2942] dark:bg-slate-950 dark:border-r dark:border-slate-800 text-slate-100 flex flex-col transition-transform ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="px-4 py-4 border-b border-white/10 flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-lg bg-teal-500 flex items-center justify-center font-heading font-extrabold text-white shrink-0">P</div>
          <div className="min-w-0">
            <div className="font-heading font-bold text-sm leading-tight truncate">PRABHAT</div>
            <div className="text-[10px] text-slate-400 truncate">Consultancy Finance ERP</div>
          </div>
          <button className="ml-auto lg:hidden" onClick={() => setMobileOpen(false)}><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-2 px-2">
          {NAV.map((group, gi) => (
            <div key={gi}>
              {group.section && <div className="text-[10px] font-bold tracking-widest text-slate-400 uppercase px-3 pt-4 pb-1">{group.section}</div>}
              {group.items.filter((it) => !it.admin || isAdmin).map((it) => (
                <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setMobileOpen(false)}
                  data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm mb-0.5 transition-all ${isActive ? "bg-slate-800/80 text-teal-400 font-semibold border-l-4 border-teal-500" : "text-slate-300 hover:bg-slate-800/40 hover:text-white border-l-4 border-transparent"}`}>
                  <it.icon className="h-4 w-4 shrink-0" /> {it.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-teal-600 flex items-center justify-center text-xs font-bold">{initials}</div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold truncate">{user?.name}</div>
            <div className="text-[10px] text-slate-400">{roleLabel}</div>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="text-slate-400 hover:text-white"><LogOut className="h-4 w-4" /></button>
        </div>
      </aside>
      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-white/90 dark:bg-slate-900/90 backdrop-blur border-b border-slate-200 dark:border-slate-800 px-4 py-2.5 flex items-center gap-3">
          <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
          <div className="hidden md:flex items-center gap-2 text-sm text-slate-400 flex-1">
            <Search className="h-4 w-4" />
            <input onKeyDown={(e) => { if (e.key === "Enter" && e.target.value) navigate(`/transactions?search=${encodeURIComponent(e.target.value)}`); }}
              data-testid="global-search" placeholder="Search transactions, candidates, receipts..." className="bg-transparent outline-none w-full text-slate-700 dark:text-slate-200" />
          </div>
          <div className="flex-1 md:hidden" />
          <div className="relative">
            <Button size="sm" onClick={() => setQuickOpen((o) => !o)} data-testid="quick-actions-btn" className="bg-teal-600 hover:bg-teal-700 gap-1.5 h-9">
              <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Quick Action</span> <ChevronDown className="h-3 w-3" />
            </Button>
            {quickOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setQuickOpen(false)} />
                <div className="absolute right-0 mt-1 w-56 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl z-40 py-1">
                  {QUICK.map((q) => (
                    <button key={q.type} data-testid={`quick-${q.type}`} onClick={() => { open(q.type); setQuickOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">
                      <q.icon className="h-4 w-4 text-teal-600" /> {q.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <button onClick={() => setDark((d) => !d)} data-testid="theme-toggle" className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <div className="relative">
            <button onClick={() => setAlertsOpen((o) => !o)} data-testid="alerts-bell" className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 relative">
              <Bell className="h-4 w-4" />
              {alerts.length > 0 && <span className="absolute top-0 right-0 h-4 min-w-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center">{alerts.length}</span>}
            </button>
            {alertsOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setAlertsOpen(false)} />
                <div className="absolute right-0 mt-1 w-80 max-h-96 overflow-y-auto bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl z-40">
                  <div className="px-4 py-2 text-xs font-bold uppercase text-slate-500 border-b border-slate-100 dark:border-slate-800">Smart Alerts</div>
                  {alerts.length === 0 ? <div className="p-6 text-sm text-slate-400 text-center">All clear — no alerts</div> :
                    alerts.map((a, i) => (
                      <button key={i} data-testid="alert-item" onClick={() => { setAlertsOpen(false); navigate(a.link); }}
                        className="w-full text-left px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800 border-b border-slate-50 dark:border-slate-800/60 flex gap-2">
                        <AlertTriangle className={`h-4 w-4 shrink-0 mt-0.5 ${a.severity === "high" ? "text-rose-500" : a.severity === "medium" ? "text-amber-500" : "text-slate-400"}`} />
                        <span className="text-xs text-slate-600 dark:text-slate-300">{a.message}</span>
                      </button>
                    ))}
                </div>
              </>
            )}
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 pb-24 lg:pb-6 max-w-[1600px] w-full mx-auto">{children}</main>
      </div>

      {/* Mobile dock */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-[#0F2942]/95 dark:bg-slate-950/95 backdrop-blur border-t border-slate-800 p-1.5 flex justify-around lg:hidden">
        {[{ t: "receive", l: "Receive", i: Receipt }, { t: "expense", l: "Expense", i: Wallet }, { t: "advance_candidate", l: "Advance", i: HandCoins }, { t: "transfer", l: "Transfer", i: ArrowLeftRight }].map((q) => (
          <button key={q.t} data-testid={`dock-${q.t}`} onClick={() => open(q.t)} className="flex flex-col items-center p-1.5 text-slate-300 active:scale-95 text-[10px]">
            <q.i className="h-5 w-5 mb-0.5" /> {q.l}
          </button>
        ))}
        <button onClick={() => navigate("/transactions")} className="flex flex-col items-center p-1.5 text-slate-300 text-[10px]"><Search className="h-5 w-5 mb-0.5" /> Search</button>
      </div>
    </div>
  );
}
